import * as Vesting from '../../../contracts/vesting'
import { amountFromWei } from '../../../utils/amount'
import { Allocation } from './types'
import { theseus } from '../../../api/theseus'
import { toast } from 'react-toastify'
import { parseChainId } from '../../../utils'
import { ethers } from 'ethers'

export const formatAllocation = async (
  allocation: any,
  account: string,
  provider: ethers.BrowserProvider | ethers.JsonRpcProvider,
) => {
  const { pool, vestingContractAddress, chainId: chainIdString } = allocation

  if (!pool?.poolAddress || !account) return

  const isVestingBlueprint = Boolean(allocation?.isVestingBlueprint)

  const chainId = parseChainId(pool?.chainID)

  const logoUrl = pool?.about?.logo?.url
  const ticker = pool?.ticker

  const defaultAllocation = {
    chainId,
    poolAddress: pool.poolAddress,
    name: pool.name,
    logoUrl,
    points: allocation.points,
    ticker,
    index: 0,
    count: 0,
    vestingContractAddress,
    pool,
    isVestingBlueprint,
  }

  if (chainId === 9999) return defaultAllocation

  const isValid = ethers.isAddress(vestingContractAddress)
  const dynamicVestingContract = isValid
    ? new ethers.Contract(vestingContractAddress, Vesting.abi, provider)
    : null

  if (isVestingBlueprint) return defaultAllocation

  if (chainIdString && !isVestingBlueprint) {
    if (dynamicVestingContract) {
      try {
        const pointsFromSc = await dynamicVestingContract?.getVestingPoints()

        const userDetails = await dynamicVestingContract?.userdetails(account)

        const index = parseInt(userDetails?.['index'])
        const totalAmountForUser = userDetails?.['userDeposit']
        const userTotalAmountInEth = amountFromWei(totalAmountForUser)

        let count = 0

        const points = pointsFromSc.map((point: any) => {
          const [datetimeRaw, percentageRaw] = point

          const datetime = new Date(datetimeRaw * 1000).getTime()
          const percentage = parseInt(percentageRaw) / 100
          const amount = userTotalAmountInEth.mul(percentage / 100)

          if (datetime < new Date().getTime()) count++

          return { datetime, percentage, amount }
        })

        if (totalAmountForUser !== '0') {
          return {
            chainId,
            poolAddress: pool.poolAddress,
            name: pool.name,
            logoUrl,
            points,
            ticker,
            index,
            count,
            vestingContractAddress,
            pool,
            isVestingBlueprint,
          }
        }
      } catch (e) {
        console.error(e)
      }
    } else {
      if (allocation?.externalVesting?.[0]?.isExternal || isVestingBlueprint) {
        return // allow for now - we might want to add this later
      } else {
        return
      }
    }
  }
}

export const getAllocations = async (
  account: string,
  provider: ethers.BrowserProvider | ethers.JsonRpcProvider,
): Promise<Allocation[]> => {
  const allocations = await theseus('/vesting/labs/all')
    .then(({ data }) => data?.data)
    .catch((e) => {
      console.error(e)
      toast.error('Error fetching allocations')
      return []
    })

  const formatted: any = []

  if (!allocations || allocations.length === 0) return formatted

  for (let i = 0; i < allocations.length; i++) {
    const allocation = allocations[i]
    const formattedAllocation = await formatAllocation(
      allocation,
      account,
      provider,
    )

    if (!formattedAllocation) continue
    formatted.push(formattedAllocation)
  }

  return formatted
}

export const claimVesting = async (
  provider: ethers.BrowserProvider,
  account: string,
  vestingContractAddress: string,
): Promise<void> => {
  const isValid = ethers.isAddress(vestingContractAddress)

  const dynamicVestingContract = isValid
    ? new ethers.Contract(vestingContractAddress, Vesting.abi, provider)
    : null

  const gas = await dynamicVestingContract?.claim.estimateGas()
  await dynamicVestingContract?.claim({ gasLimit: gas })
}
